// Hook for verify mode state and handlers
// This encapsulates all the logic for the "Verify Image" tab

import { useState, useRef, useCallback } from "react";
import type {
  VerifyResult,
  ZkProofVerification,
  RawEmailVerification,
  SimilarMatch,
} from "../types";
import { verifyImage as apiVerifyImage, verifyEmail } from "../api/client";
import { verifyProofData } from "proteus-kit/zk-proof";

export interface UseVerifyModeReturn {
  // State
  image: File | null;
  preview: string | null;
  loading: boolean;
  loadingStatus: string;
  result: VerifyResult | null;
  zkProofVerification: ZkProofVerification;
  rawEmailVerification: RawEmailVerification;
  similarMatches: SimilarMatch[];

  // Actions
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleRefresh: () => Promise<void>;
  reset: () => void;

  // Refs
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

export function useVerifyMode(): UseVerifyModeReturn {
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<string>("");
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [similarMatches, setSimilarMatches] = useState<SimilarMatch[]>([]);
  const [zkProofVerification, setZkProofVerification] =
    useState<ZkProofVerification>({ performed: false });
  const [rawEmailVerification, setRawEmailVerification] =
    useState<RawEmailVerification>({ performed: false });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const reset = useCallback(() => {
    setImage(null);
    setPreview(null);
    setResult(null);
    setLoadingStatus("");
    setSimilarMatches([]);
    setZkProofVerification({ performed: false });
    setRawEmailVerification({ performed: false });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const verifyImageFile = useCallback(async (file: File) => {
    setLoading(true);
    setLoadingStatus("Uploading image...");
    setZkProofVerification({ performed: false });
    setRawEmailVerification({ performed: false });

    try {
      setLoadingStatus("Extracting metadata...");
      const verifyResult = await apiVerifyImage(
        await file.arrayBuffer(),
        file.type
      );
      setResult(verifyResult);

      // Extract similar matches from verification result
      if (verifyResult.verification?.similarMatches) {
        setSimilarMatches(verifyResult.verification.similarMatches);
      } else {
        setSimilarMatches([]);
      }

      // Verify ZK proof if present in metadata
      if (verifyResult.hasStoredMetadata && verifyResult.metadata?.proof) {
        setLoadingStatus("Verifying ZK proof...");
        setZkProofVerification({ performed: false, verifying: true });

        try {
          const zkResult = await verifyProofData(verifyResult.metadata.proof);

          // Compare the signed hash with computed hash
          const computedHash = verifyResult.verification?.computed?.dinohash512;
          const signedHash = zkResult.dinoHash;
          const hashMatch =
            computedHash && signedHash
              ? computedHash === signedHash
              : undefined;

          setZkProofVerification({
            performed: true,
            valid: zkResult.valid,
            error: zkResult.error,
            emailAddress: zkResult.emailAddress,
            dinoHash: zkResult.dinoHash,
            hashMatch,
            verifying: false,
          });
        } catch (zkError) {
          setZkProofVerification({
            performed: true,
            valid: false,
            error:
              zkError instanceof Error
                ? zkError.message
                : "ZK verification failed",
            verifying: false,
          });
        }
      }

      // Verify raw email if present in metadata (and no ZK proof)
      if (
        verifyResult.hasStoredMetadata &&
        verifyResult.metadata?.raw_email &&
        !verifyResult.metadata?.proof
      ) {
        setLoadingStatus("Verifying DKIM signature...");
        setRawEmailVerification({ performed: false, verifying: true });

        try {
          const emailResult = await verifyEmail(verifyResult.metadata.raw_email);

          // Compare the signed hash with computed hash
          const computedHash = verifyResult.verification?.computed?.dinohash512;
          const signedHash = emailResult.dinoHash;
          const hashMatch =
            computedHash && signedHash
              ? computedHash === signedHash
              : undefined;

          setRawEmailVerification({
            performed: true,
            valid: emailResult.valid,
            error: emailResult.error,
            emailAddress: emailResult.emailAddress,
            dinoHash: emailResult.dinoHash,
            domain: emailResult.domain,
            hashMatch,
            verifying: false,
          });
        } catch (emailError) {
          setRawEmailVerification({
            performed: true,
            valid: false,
            error:
              emailError instanceof Error
                ? emailError.message
                : "Email verification failed",
            verifying: false,
          });
        }
      }
    } catch (err) {
      setResult({
        found: false,
        message: err instanceof Error ? err.message : "An error occurred",
      });
      setSimilarMatches([]);
      setZkProofVerification({ performed: false });
      setRawEmailVerification({ performed: false });
    } finally {
      setLoading(false);
      setLoadingStatus("");
    }
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setImage(file);
        setPreview(URL.createObjectURL(file));
        setResult(null);
        verifyImageFile(file);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = "";
    },
    [verifyImageFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith("image/")) {
        setImage(file);
        setPreview(URL.createObjectURL(file));
        setResult(null);
        verifyImageFile(file);
      }
    },
    [verifyImageFile]
  );

  const handleRefresh = useCallback(async () => {
    if (image) {
      await verifyImageFile(image);
    }
  }, [image, verifyImageFile]);

  return {
    image,
    preview,
    loading,
    loadingStatus,
    result,
    zkProofVerification,
    rawEmailVerification,
    similarMatches,
    handleFileSelect,
    handleDrop,
    handleRefresh,
    reset,
    fileInputRef,
  };
}
